import React, { useState } from 'react';
import { api } from '../api';
import {
  demoCompleteness, systemsCompleteness,
  exteriorCompleteness, landscapeCompleteness, roomCompleteness,
  computeSOWCompleteness,
} from '../computeSOWCompleteness';
import DemoSection from './DemoSection';
import SystemsSection from './SystemsSection';
import ExteriorSection from './ExteriorSection';
import LandscapeSection from './LandscapeSection';
import RoomCard from './RoomCard';

const ROOM_TYPES = [
  ['master_bedroom','Master Bedroom'],
  ['bedroom',       'Bedroom'],
  ['master_bath',   'Master Bath'],
  ['bathroom',      'Bathroom'],
  ['kitchen',       'Kitchen'],
  ['living',        'Living Room'],
  ['dining',        'Dining Room'],
  ['laundry',       'Laundry'],
  ['hallway',       'Hallway'],
  ['garage',        'Garage'],
  ['other',         'Other'],
];

export default function SOWSection({ deal, onUpdate }) {
  const rooms = deal.rooms || [];
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('bedroom');

  const totalSqFt = rooms.reduce((s, r) => s + (r.sq_ft || (r.length_ft && r.width_ft ? r.length_ft * r.width_ft : 0)), 0);
  const closetSqFt = rooms.filter(r => r.has_closet).reduce((s, r) => s + (r.closet_sq_ft || (r.closet_length && r.closet_width ? r.closet_length * r.closet_width : 0)), 0);

  const handleAdd = async () => {
    const name = newName.trim() || ROOM_TYPES.find(([v]) => v === newType)?.[1] || 'Room';
    await api.addRoom(deal.id, { name, room_type: newType });
    setNewName('');
    setNewType('bedroom');
    setAdding(false);
    onUpdate();
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Remove this room?')) return;
    await api.deleteRoom(deal.id, roomId);
    onUpdate();
  };

  // Compute completeness for all sections
  const completeness = computeSOWCompleteness(deal);
  const demoStatus  = completeness.sections.demo.status;
  const sysStatus   = completeness.sections.systems.status;
  const extStatus   = completeness.sections.exterior.status;
  const landStatus  = completeness.sections.landscape.status;

  const statusColor = { done: '#27ae60', partial: '#e67e22', missing: '#d63031' };
  const statusLabel = { done: '✓ Assessed', partial: '⚠ Partial', missing: '⚠ Not Started' };

  return (
    <div>
      {/* Summary bar */}
      <div style={{display:'flex',gap:20,background:'var(--brand)',color:'#fff',
        borderRadius:'var(--radius)',padding:'12px 20px',marginBottom:20}}>
        <div>
          <div style={{fontSize:11,opacity:0.8,marginBottom:2}}>Rooms</div>
          <div style={{fontWeight:700,fontSize:18}}>{rooms.length}</div>
        </div>
        <div>
          <div style={{fontSize:11,opacity:0.8,marginBottom:2}}>Total Measured Sq Ft</div>
          <div style={{fontWeight:700,fontSize:18}}>{totalSqFt > 0 ? totalSqFt.toFixed(0) : '—'}</div>
        </div>
        {closetSqFt > 0 && (
          <div>
            <div style={{fontSize:11,opacity:0.8,marginBottom:2}}>Closet Sq Ft</div>
            <div style={{fontWeight:700,fontSize:18}}>{closetSqFt.toFixed(0)}</div>
          </div>
        )}
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <div style={{fontSize:11,opacity:0.8,marginBottom:2}}>SOW Completion</div>
          <div style={{fontWeight:700,fontSize:18}}>{completeness.score}%</div>
          {completeness.allMissing.length > 0 && (
            <div style={{fontSize:10,opacity:0.75}}>{completeness.allMissing.length} items need review</div>
          )}
        </div>
      </div>

      {/* ── Demo & Disposal ── */}
      <DemoSection deal={deal} onUpdate={onUpdate} status={demoStatus} statusLabel={statusLabel[demoStatus]} statusColor={statusColor[demoStatus]} />

      {/* ── Major Systems ── */}
      <SystemsSection deal={deal} onUpdate={onUpdate} status={sysStatus} statusLabel={statusLabel[sysStatus]} statusColor={statusColor[sysStatus]} />

      {/* ── Exterior ── */}
      <ExteriorSection deal={deal} onUpdate={onUpdate} status={extStatus} statusLabel={statusLabel[extStatus]} statusColor={statusColor[extStatus]} />

      {/* ── Landscape & Hardscape ── */}
      <LandscapeSection deal={deal} onUpdate={onUpdate} status={landStatus} statusLabel={statusLabel[landStatus]} statusColor={statusColor[landStatus]} />

      {/* ── Room-by-Room ── */}
      <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',
        color:'var(--brand)',borderBottom:'1px solid var(--border)',paddingBottom:4,marginBottom:14,marginTop:8}}>
        Room-by-Room
      </div>

      {rooms.length === 0 && (
        <div className="empty-state">
          <div className="icon">🏠</div>
          <div>No rooms yet. Add rooms to build your scope of work.</div>
        </div>
      )}

      {rooms.map(room => {
        const rc = roomCompleteness(room);
        const statusColor = { done: '#27ae60', partial: '#e67e22', missing: '#d63031' };
        const statusLabel = { done: '✓ Assessed', partial: '⚠ Partial', missing: '⚠ Not Started' };
        return (
          <RoomCard key={room.id} room={room} dealId={deal.id} sowCosts={deal.sow_costs}
            onUpdate={onUpdate} onDelete={handleDelete}
            status={rc.status} statusLabel={statusLabel[rc.status]} statusColor={statusColor[rc.status]}
          />
        );
      })}

      {/* Add room */}
      {adding ? (
        <div className="card" style={{padding:'16px',display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{flex:'1 1 180px'}}>
            <label>Room Name</label>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Master Bedroom"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAdding(false); }} />
          </div>
          <div style={{flex:'1 1 160px'}}>
            <label>Room Type</label>
            <select value={newType} onChange={e => setNewType(e.target.value)}>
              {ROOM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button className="btn-primary" onClick={handleAdd}>Add Room</button>
          <button className="btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{width:'100%',background:'var(--brand-light)',border:'2px dashed var(--brand)',
            color:'var(--brand)',borderRadius:'var(--radius)',padding:'14px',fontWeight:600,
            fontSize:14,cursor:'pointer',marginTop:4}}>
          + Add Room
        </button>
      )}
    </div>
  );
}
